import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Plus, Eye, Edit, CheckCircle2, Filter } from 'lucide-react';
import { listLegalDocs, createLegalDoc, updateLegalDoc, publishLegalDoc } from '../../api/legalAdmin';
import { getLegalTypes } from '../../api/legal';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Input, Select } from '../../components/ui/Input';
import { SimpleHtmlEditor } from '../../components/admin/SimpleHtmlEditor';

const STATUS_LABELS = { draft: 'Taslak', published: 'Yayında', archived: 'Arşiv' };
const STATUS_COLORS = {
  draft: 'bg-amber-100 text-amber-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-slate-100 text-slate-500',
};

export function LegalDocsAdminPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    document_type: '',
    slug: '',
    version: '1.0',
    title: '',
    content_html: '',
  });
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  // Detail/Edit modal
  const [detailDoc, setDetailDoc] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Publish modal
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishSaving, setPublishSaving] = useState(false);
  const [publishError, setPublishError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      const d = await listLegalDocs(params);
      setDocs(d.documents || []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => {
    getLegalTypes().then(d => setTypes(d.types || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!createForm.document_type || !createForm.slug || !createForm.title || !createForm.content_html) {
      return setCreateError('Tip, slug, başlık ve içerik zorunlu');
    }
    setCreateSaving(true);
    try {
      await createLegalDoc(createForm);
      setCreateOpen(false);
      setCreateForm({ document_type: '', slug: '', version: '1.0', title: '', content_html: '' });
      await load();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Oluşturulamadı');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm || !detailDoc) return;
    setEditSaving(true);
    setEditError('');
    try {
      await updateLegalDoc(detailDoc.id, {
        title: editForm.title,
        content_html: editForm.content_html,
      });
      setDetailDoc(null);
      setEditForm(null);
      await load();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Güncellenemedi');
    } finally {
      setEditSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!detailDoc) return;
    setPublishSaving(true);
    setPublishError('');
    try {
      await publishLegalDoc(detailDoc.id);
      setPublishOpen(false);
      setDetailDoc(null);
      setEditForm(null);
      await load();
    } catch (err) {
      setPublishError(err.response?.data?.error || 'Yayınlanamadı');
    } finally {
      setPublishSaving(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/ops-center-f9x2')}
              className="text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft size={20} />
            </button>
            <FileText size={22} className="text-blue-600" />
            <h1 className="text-xl font-bold text-slate-800">Yasal Dokümanlar</h1>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={16} /> Yeni Doküman
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Filtreler */}
        <div className="flex items-center gap-3 mb-4">
          <Filter size={16} className="text-slate-400" />
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-48"
          >
            <option value="">Tüm tipler</option>
            {types.map(t => (
              <option key={t.code} value={t.code}>{t.name}</option>
            ))}
          </Select>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-40"
          >
            <option value="">Tüm durumlar</option>
            <option value="draft">Taslak</option>
            <option value="published">Yayında</option>
            <option value="archived">Arşiv</option>
          </Select>
        </div>

        {/* Tablo */}
        {loading && <p className="text-slate-500">Yükleniyor...</p>}

        {!loading && docs.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 mb-4">Doküman bulunamadı</p>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus size={16} /> İlk Dokümanı Oluştur
            </Button>
          </div>
        )}

        {!loading && docs.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Tip</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Başlık</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Slug</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Versiyon</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Durum</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-semibold">Yürürlük</th>
                  <th className="text-right px-4 py-3 text-slate-600 font-semibold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => (
                  <tr key={doc.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700 text-xs">{doc.document_type}</td>
                    <td className="px-4 py-3 text-slate-800 font-medium">{doc.title}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{doc.slug}</td>
                    <td className="px-4 py-3 text-slate-700">{doc.version}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[doc.status]}`}>
                        {STATUS_LABELS[doc.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{formatDate(doc.effective_date)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          setDetailDoc(doc);
                          setEditForm({ title: doc.title, content_html: doc.content_html || '' });
                          setEditError('');
                        }}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        {doc.status === 'draft'
                          ? <><Edit size={12} className="inline mr-1" /> Düzenle</>
                          : <><Eye size={12} className="inline mr-1" /> Görüntüle</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Yeni Doküman" size="xl">
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Tip *</label>
              <Select
                value={createForm.document_type}
                onChange={(e) => setCreateForm(f => ({ ...f, document_type: e.target.value }))}
                required
              >
                <option value="">Seçin...</option>
                {types.map(t => (
                  <option key={t.code} value={t.code}>{t.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Versiyon *</label>
              <Input
                value={createForm.version}
                onChange={(e) => setCreateForm(f => ({ ...f, version: e.target.value }))}
                placeholder="1.0"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Slug *</label>
              <Input
                value={createForm.slug}
                onChange={(e) => setCreateForm(f => ({ ...f, slug: e.target.value }))}
                placeholder="kvkk-aydinlatma"
                required
              />
              <p className="text-xs text-slate-500 mt-1">URL'de görünecek (a-z, 0-9, tire)</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Başlık *</label>
              <Input
                value={createForm.title}
                onChange={(e) => setCreateForm(f => ({ ...f, title: e.target.value }))}
                placeholder="KVKK Aydınlatma Metni"
                required
              />
            </div>
          </div>

          <SimpleHtmlEditor
            label="İçerik *"
            value={createForm.content_html}
            onChange={(v) => setCreateForm(f => ({ ...f, content_html: v }))}
            rows={15}
          />

          {createError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {createError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="secondary" type="button" onClick={() => setCreateOpen(false)}>Vazgeç</Button>
            <Button type="submit" loading={createSaving}>Taslak Olarak Kaydet</Button>
          </div>
        </form>
      </Modal>

      {/* Detail/Edit Modal */}
      <Modal
        open={!!detailDoc}
        onClose={() => { setDetailDoc(null); setEditForm(null); setEditError(''); }}
        title={detailDoc?.status === 'draft' ? 'Doküman Düzenle' : 'Doküman Görüntüle'}
        size="xl"
      >
        {detailDoc && editForm && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-3 text-xs grid grid-cols-3 gap-2">
              <div><span className="text-slate-500">Tip:</span> <span className="font-medium">{detailDoc.document_type}</span></div>
              <div><span className="text-slate-500">Slug:</span> <span className="font-mono">{detailDoc.slug}</span></div>
              <div><span className="text-slate-500">Versiyon:</span> <span className="font-medium">{detailDoc.version}</span></div>
              <div><span className="text-slate-500">Durum:</span> <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[detailDoc.status]}`}>{STATUS_LABELS[detailDoc.status]}</span></div>
              <div><span className="text-slate-500">Yürürlük:</span> <span>{formatDate(detailDoc.effective_date)}</span></div>
              <div><span className="text-slate-500">Yayın:</span> <span>{formatDate(detailDoc.published_at)}</span></div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Başlık</label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                disabled={detailDoc.status !== 'draft'}
              />
            </div>

            {detailDoc.status === 'draft' ? (
              <SimpleHtmlEditor
                label="İçerik"
                value={editForm.content_html}
                onChange={(v) => setEditForm(f => ({ ...f, content_html: v }))}
                rows={15}
              />
            ) : (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">İçerik (salt okunur)</label>
                <div
                  className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-h-[400px] overflow-y-auto prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: editForm.content_html }}
                />
              </div>
            )}

            {editError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {editError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="secondary" onClick={() => { setDetailDoc(null); setEditForm(null); }}>
                Kapat
              </Button>
              {detailDoc.status === 'draft' && (
                <>
                  <Button variant="secondary" onClick={handleSaveEdit} loading={editSaving}>
                    Kaydet
                  </Button>
                  <Button onClick={() => { setPublishOpen(true); setPublishError(''); }}>
                    <CheckCircle2 size={16} /> Yayınla
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Publish Confirm Modal */}
      <Modal open={publishOpen} onClose={() => setPublishOpen(false)} title="Dokümanı Yayınla" size="md">
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <strong>Dikkat:</strong> Yayınladığınızda:
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              <li>Aynı tip + dilde yayında olan eski doküman otomatik arşivlenecek</li>
              <li>Eski versiyonu onaylayan kullanıcıların onayları geçersiz olacak (CONSENT_OUTDATED)</li>
              <li>Bu işlem geri alınamaz</li>
            </ul>
          </div>

          <p className="text-sm text-slate-600">
            Yürürlük tarihi: dokümanda set edilmemişse şu an (NOW) kullanılır.
          </p>

          {publishError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {publishError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="secondary" onClick={() => setPublishOpen(false)}>Vazgeç</Button>
            <Button onClick={handlePublish} loading={publishSaving}>
              <CheckCircle2 size={16} /> Yayınla
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
